import type { ColumnEntry } from "@/v2/framework/column/types";
import type { DynamicColumnSlot } from "@/v2/framework/view/types";
import { FieldType, ColumnIconType } from "@/v2/framework/column/types";
import { t } from "@/services/i18n";
import {
  sportDefaultEventSelectionOptions,
  sportGamesStatusOptions,
  sportStatusOptions,
} from "../shared";
import { getSupplementalsByEntity } from "@/services/supplementals";

type SportGameSlice = {
  status?: number;
  position?: number | string | null;
  displayIds?: { prefix?: string; rangeFrom?: number | string; rangeTo?: number | string }[];
  defaultEventSelection?: number;
  defaultSelection?: number;
};

type SportGridRow = {
  code?: string;
  status?: number;
  prematchGame?: SportGameSlice;
  liveGame?: SportGameSlice;
  supplementalNames?: { supplementalNameTypeId: string; name: string }[];
};

function gameStatusLabel(status: number | undefined, options: typeof sportGamesStatusOptions) {
  const match = options.find((option) => option.value === status);
  return t(match?.title ?? "");
}

function gameStatusIcon(status: number | undefined, options: typeof sportGamesStatusOptions) {
  const match = options.find((option) => option.value === status);
  return {
    code: match?.icon?.code ?? "",
    color: match?.icon?.color ?? "",
  };
}

function displayRangeValues(ids: SportGameSlice["displayIds"]) {
  return (ids ?? []).map((id) => {
    const prefix = id.prefix ?? "";
    const from = id.rangeFrom ?? "";
    const to = id.rangeTo ?? "";
    return `${prefix}${from}-${to}`;
  });
}

function defaultSelectionLabel(game: SportGameSlice | undefined) {
  const value = game?.defaultEventSelection ?? game?.defaultSelection;
  const match = sportDefaultEventSelectionOptions.find((option) => option.value === value);
  return match?.title ?? "";
}

export const columns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "syntheticId",
    defaultVisibility: false,
    title: "table_columns.synthetic_id",
    fieldType: FieldType.TextCell,
  },
  {
    key: "sportIcon",
    defaultVisibility: true,
    title: "table_columns.sport_icon",
    fieldType: FieldType.TextCell,
    icon: {
      type: ColumnIconType.Main,
      formatter: (row: unknown) => {
        const item = row as SportGridRow;
        return { code: `${item.code ?? ""}` };
      },
    },
  },
  {
    key: "sourceTenantName",
    defaultVisibility: false,
    title: "table_columns.source_tenant_name",
    fieldType: FieldType.TextCell,
  },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "sourceName",
    defaultVisibility: true,
    title: "table_columns.source_name",
    fieldType: FieldType.TextCell,
  },
  {
    type: "group",
    key: "prematch",
    title: "table_columns.prematch_group",
    columns: [
      {
        key: "prematchGameStatus",
        defaultVisibility: true,
        title: "table_columns.prematch_status",
        fieldType: FieldType.TextCell,
        valueFormatter: (row: unknown) =>
          gameStatusLabel((row as SportGridRow).prematchGame?.status, sportGamesStatusOptions),
        icon: {
          type: ColumnIconType.Prepend,
          formatter: (row: unknown) =>
            gameStatusIcon((row as SportGridRow).prematchGame?.status, sportGamesStatusOptions),
        },
      },
      {
        key: "prematchGamePosition",
        defaultVisibility: true,
        title: "table_columns.prematch_position",
        fieldType: FieldType.InputCell,
        apiPath: "prematchGame.position",
        sort: {
          enabled: true,
          key: "prematchGamePosition",
        },
        valueFormatter: (row: unknown) => {
          const position = (row as SportGridRow).prematchGame?.position;
          return position == null ? "" : String(position);
        },
      },
      {
        key: "prematchGameDisplayRange",
        defaultVisibility: false,
        title: "table_columns.prematch_display_range",
        fieldType: FieldType.ArrayCell,
        valueFormatter: (row: unknown) =>
          displayRangeValues((row as SportGridRow).prematchGame?.displayIds),
      },
      {
        key: "prematchGameDefaultSelection",
        defaultVisibility: false,
        title: "table_columns.prematch_default_selection",
        fieldType: FieldType.TextCell,
        valueFormatter: (row: unknown) =>
          defaultSelectionLabel((row as SportGridRow).prematchGame),
      },
    ],
  },
  {
    type: "group",
    key: "live",
    title: "table_columns.live_group",
    columns: [
      {
        key: "liveGameStatus",
        defaultVisibility: true,
        title: "table_columns.live_status",
        fieldType: FieldType.TextCell,
        valueFormatter: (row: unknown) =>
          gameStatusLabel((row as SportGridRow).liveGame?.status, sportGamesStatusOptions),
        icon: {
          type: ColumnIconType.Prepend,
          formatter: (row: unknown) =>
            gameStatusIcon((row as SportGridRow).liveGame?.status, sportGamesStatusOptions),
        },
      },
      {
        key: "liveGamePosition",
        defaultVisibility: true,
        title: "table_columns.live_position",
        fieldType: FieldType.InputCell,
        apiPath: "liveGame.position",
        sort: {
          enabled: true,
          key: "liveGamePosition",
        },
        valueFormatter: (row: unknown) => {
          const position = (row as SportGridRow).liveGame?.position;
          return position == null ? "" : String(position);
        },
      },
      {
        key: "liveGameDisplayRange",
        defaultVisibility: false,
        title: "table_columns.live_display_range",
        fieldType: FieldType.ArrayCell,
        valueFormatter: (row: unknown) =>
          displayRangeValues((row as SportGridRow).liveGame?.displayIds),
      },
      {
        key: "liveGameDefaultSelection",
        defaultVisibility: false,
        title: "table_columns.live_default_selection",
        fieldType: FieldType.TextCell,
        valueFormatter: (row: unknown) => defaultSelectionLabel((row as SportGridRow).liveGame),
      },
    ],
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const item = row as SportGridRow;
      const status = sportStatusOptions.find((option) => option.value === item.status);
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const item = row as SportGridRow;
        const status = sportStatusOptions.find((option) => option.value === item.status);
        return {
          code: status?.icon?.code ?? "",
          color: status?.icon?.color ?? "",
        };
      },
    },
  },
  {
    key: "info",
    defaultVisibility: false,
    title: "table_columns.info",
    fieldType: FieldType.TextCell,
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    columnsPanelTitle: "common.labels.column_view",
    headerActions: [
      {
        icon: "table-d",
        label: "Column view",
      },
    ],
    rowActions: [
      {
        icon: "edit-ng",
        label: "Edit",
        onClick: (ctx) => ctx.openDialog("edit"),
      },
      {
        icon: "trash-ng",
        label: "Delete",
        onClick: (ctx) => ctx.openDialog("delete"),
      },
    ],
  },
];

export const dynamicColumnSlots: DynamicColumnSlot[] = [
  {
    anchor: "name",
    createColumns: () => {
      const supplementalNameTypes = getSupplementalsByEntity("SPORT");

      return supplementalNameTypes.map((item) => ({
        key: item.id,
        title: `table_columns.${item.name.toLowerCase()}`,
        fieldType: FieldType.InputCell,
        defaultVisibility: true,
        valueFormatter: (row: unknown) => {
          const r = row as SportGridRow;
          return r.supplementalNames?.find((s) => s.supplementalNameTypeId === item.id)?.name ?? "";
        },
        payloadOutFormatter: (value: unknown) => ({
          supplementalNames: [{ supplementalNameTypeId: item.id, name: value }],
        }),
      }));
    },
  },
];
